import * as React from 'react';
import { ICoreContext } from '@msdyn365-commerce/core';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';

export interface IQuantityInputProps {
    id?: string;
    context: ICoreContext;
    disable?: boolean;
    product: SimpleProduct | undefined;
    quantity: number;
    breakoff: boolean;
    min?: number;
    showOnlyIncrementDecrement?: boolean;
    showNonBreakoffAmount: boolean;
    nonBreakoffUOM?: string;
    onQuantityChange: (quantity: number) => void;
    BuyButton?: React.ReactNode;
    allowZero?: boolean;
}

function isDecimalAllowed(product: SimpleProduct | undefined, breakoff: boolean): boolean {
    // Only relevant for kg products
    if (!product || product.DefaultUnitOfMeasure !== 'kg') {
        return false;
    }

    const multipleOfQuantity = product.Behavior?.MultipleOfQuantity;

    // If the product has breakoff available only allow if breakoff is enabled
    if (multipleOfQuantity && multipleOfQuantity > 1) {
        return breakoff;
    }

    // Otherwise always allow decimal
    return true;
}

const QuantityInput = (props: IQuantityInputProps) => {
    const { disable, product, quantity, breakoff, min, onQuantityChange, allowZero } = props;

    const [endsWithDecimal, setEndsWithDecimal] = React.useState(false);
    const [showEmpty, setShowEmpty] = React.useState(false);

    React.useEffect(() => {
        // Update quantity to 1, respecting the multiplier if applicable
        if (!product || allowZero || quantity > 0) {
            return;
        }
        const multiplier = breakoff ? 1 : product.Behavior?.MultipleOfQuantity || 1;
        onQuantityChange(validateQuantity(1 * multiplier));
    }, [product]);

    const baseClassName = 'quantity-input';
    // Check if there is a friendly UOM defined on the SimpleProduct

    const allowDecimal = isDecimalAllowed(product, breakoff);

    // const selectedUOM = breakoff ? uom : 'KASSE(R)';
    const multiplier = breakoff ? 1 : product?.Behavior?.MultipleOfQuantity || 1;
    const minimumQuantity = (min || +!allowZero) * multiplier;

    const validateQuantity = (quantity: number): number => {
        const newQuantity = Math.max(minimumQuantity, quantity);
        return Math.round(newQuantity * 100) / 100; // Round to two decimal places
    };

    const onIncrement = () => {
        const newQuantity = validateQuantity(quantity + multiplier);

        onQuantityChange(newQuantity);
    };

    const onDecrement = () => {
        const newQuantity = validateQuantity(Math.max(0, quantity - multiplier));

        onQuantityChange(newQuantity);
    };

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        // Preserve the decimal character at the end if the user is typing a decimal number
        setEndsWithDecimal(e.target.value.endsWith('.'));
        setShowEmpty(e.target.value === '');

        // We're essentially changing the displayQuantity instead of the actual quantity
        const parsedValue = parseFloat(e.target.value) || 0;
        const newQuantity = breakoff ? validateQuantity(parsedValue) : validateQuantity(parsedValue * multiplier);

        onQuantityChange(newQuantity);
    };

    // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
    const displayQuantity = showEmpty ? '' : product && parseFloat((quantity / multiplier).toFixed(2)) + (endsWithDecimal ? '.' : '');

    return (
        <div className={baseClassName}>
            <div className={`${baseClassName}__quantity-container`}>
                <div className={`${baseClassName}__quantity-controls`}>
                    <button
                        className={`${baseClassName}__quantity-controls__minus`}
                        disabled={disable || !product || quantity === minimumQuantity}
                        onClick={onDecrement}
                    >
                        -
                    </button>
                    <input
                        className={`${baseClassName}__quantity-controls__input`}
                        disabled={disable}
                        min={min}
                        max={1000}
                        onKeyPress={e => {
                            if (e.key === '.' && allowDecimal) {
                                return;
                            }

                            if (!/[0-9]/.test(e.key)) {
                                e.preventDefault();
                            }
                        }}
                        onChange={onInputChange}
                        onBlur={e => {
                            // If the input is empty, set it to 0
                            if (e.target.value === '') {
                                onInputChange({ target: { value: '0' } } as React.ChangeEvent<HTMLInputElement>);
                            }
                        }}
                        value={displayQuantity}
                    />
                    <button className={`${baseClassName}__quantity-controls__plus`} disabled={disable || !product} onClick={onIncrement}>
                        +
                    </button>
                </div>

                <div className={`${baseClassName}__buy-container`}>{props.BuyButton}</div>
            </div>
        </div>
    );
};

export default QuantityInput;
